import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Shield, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function AffiliateDisclosure() {
  return (
    <Layout>
      <Helmet>
        <title>Affiliate Disclosure | Transparency Badge</title>
        <meta name="description" content="How we use affiliate links on Transparency Badge, and why they never affect your scan results." />
        <link rel="canonical" href="https://transparencybadge.com/affiliate-disclosure" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Affiliate Disclosure"
            title="Affiliate Disclosure"
            description="A quick, honest explanation of how we make money from some of the links on this site."
          />
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container max-w-2xl">

          <div className="bg-safe-bg border border-safe/30 rounded-2xl p-8 mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-safe text-safe-foreground">
                <Shield className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-bold text-foreground">The short version</h2>
            </div>
            <p className="text-muted-foreground">
              Some links on this site are affiliate links. If you click one and buy something, we may earn a small commission — at no extra cost to you. This never affects a scan result. Whether a site comes back green, yellow, or red is decided purely by our checks, before any product recommendation is shown.
            </p>
          </div>

          <div className="space-y-8">
            <div>
              <h2 className="text-lg font-bold text-foreground mb-3">Where you'll see these links</h2>
              <p className="text-muted-foreground">
                We occasionally recommend security tools — things like VPNs, password managers, or malware scanners — in a separate section below your scan result, and sometimes in blog posts. We only recommend products we think are genuinely useful for staying safe online, not just ones that pay the most.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-3">Why we do this</h2>
              <p className="text-muted-foreground">
                Running the safety checker, keeping threat data current, and reviewing verified badge applications all cost time and money. Affiliate commissions are one of the ways we keep the checker free for everyone to use, without needing to charge visitors or clutter the site with unrelated ads.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-bold text-foreground mb-3">What stays independent</h2>
              <p className="text-muted-foreground">
                Our scan results, domain checks, and verified badge decisions are never influenced by affiliate relationships. A site doesn't get flagged as risky to sell you something, and a site doesn't get a better result because we work with a company. If that ever changed, we'd tell you here.
              </p>
            </div>

            <div className="bg-card rounded-xl border p-6">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-semibold text-foreground">Questions?</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                If anything here is unclear, or you just want to ask how a specific recommendation works, email us at{" "}
                <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline">
                  hello@transparencybadge.com
                </a>
                . We're happy to explain.
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              See also our{" "}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>{" "}
              and{" "}
              <Link to="/disclaimer" className="text-primary hover:underline">Disclaimer</Link>.
            </p>
          </div>

        </div>
      </section>
    </Layout>
  );
}
